/**
 * Idle Screen Tab Component
 * Upload and manage idle screen media (images or Lottie) for 4-inch and 5-inch displays
 */

import React, { useCallback } from 'react';
import { Monitor, Eye, EyeOff } from 'lucide-react';
import { useKioskCustomizerStore } from '../../stores/kiosk-customizer-store';
import { MediaUploadZone } from '../../../core/components/shared/MediaUploadZone';
import { LogoCropDialog } from '../../../core/components/shared/LogoCropDialog';
import { validateMediaFile, createFilePreviewUrl } from '../../../core/utils/file-helpers';
import { PendingMediaUpload } from '../../types/kiosk-types';
import { Card } from '@/core/components/ui/card';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Label } from '@/core/components/ui/label';
import { toast } from 'sonner';

export function IdleScreenTab() {
  const {
    config,
    pendingMedia4IdleScreen,
    pendingMedia5IdleScreen,
    showIdleScreenInPreview,
    showIdleScreenCropDialog,
    idleScreenCropDialogTarget,
    setPendingIdleScreen,
    clearPendingIdleScreen,
    updateIdleScreenCropArea,
    openIdleScreenCropDialog,
    closeIdleScreenCropDialog,
    toggleIdleScreenVisibility,
  } = useKioskCustomizerStore();

  // Handle file selection for idle screen
  const handleIdleScreenSelect = useCallback(
    (target: 'media4' | 'media5') => (file: File) => {
      const validation = validateMediaFile(file);

      if (!validation.valid) {
        toast.error('Invalid File', {
          description: validation.error,
        });
        return;
      }

      const previewUrl = createFilePreviewUrl(file);
      const upload: PendingMediaUpload = {
        file,
        mediaType: validation.mediaType || 'image',
        originalImageUrl: previewUrl,
        previewUrl,
      };

      setPendingIdleScreen(target, upload);
      toast.success('File uploaded', {
        description: `${validation.mediaType === 'lottie' ? 'Lottie animation' : 'Image'} ready for preview`,
      });
    },
    [setPendingIdleScreen]
  );

  // Handle crop for idle screen images
  const handleIdleScreenCrop = useCallback(
    (target: 'media4' | 'media5') => () => {
      openIdleScreenCropDialog(target);
    },
    [openIdleScreenCropDialog]
  );

  // Handle remove idle screen
  const handleIdleScreenRemove = useCallback(
    (target: 'media4' | 'media5') => () => {
      clearPendingIdleScreen(target);
      toast.info('File removed', {
        description: 'Idle screen media has been removed',
      });
    },
    [clearPendingIdleScreen]
  );

  // Get current idle screen for crop dialog
  const currentIdleScreenForCrop =
    idleScreenCropDialogTarget === 'media4' ? pendingMedia4IdleScreen : pendingMedia5IdleScreen;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Monitor className="h-5 w-5 text-primary" />
        <div>
          <h3 className="text-lg font-semibold">Idle Screen Media</h3>
          <p className="text-sm text-gray-600">
            Upload images or Lottie animations to display when kiosk is idle
          </p>
        </div>
      </div>

      {/* Visibility Toggle */}
      <Card className="p-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="show-idle-screen"
            checked={showIdleScreenInPreview}
            onCheckedChange={(checked) => toggleIdleScreenVisibility(!!checked)}
          />
          <Label
            htmlFor="show-idle-screen"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center gap-2"
          >
            {showIdleScreenInPreview ? (
              <Eye className="h-4 w-4 text-green-600" />
            ) : (
              <EyeOff className="h-4 w-4 text-gray-400" />
            )}
            Show idle screen in preview
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          Toggle to preview idle screen overlay on the mockup display
        </p>
      </Card>

      {/* 4-inch Idle Screen Upload */}
      <div>
        <h4 className="text-sm font-medium mb-3 text-gray-700">4-inch Display (480x480px)</h4>
        <MediaUploadZone
          label="4-inch Idle Screen"
          target="media4"
          currentMedia={config.icons.media4IdleScreen}
          pendingMedia={pendingMedia4IdleScreen}
          onFileSelect={handleIdleScreenSelect('media4')}
          onRemove={handleIdleScreenRemove('media4')}
          onCrop={handleIdleScreenCrop('media4')}
          helpText="Upload an image or Lottie animation for the 4-inch kiosk idle screen. Images can be cropped after upload."
        />
      </div>

      {/* 5-inch Idle Screen Upload */}
      <div>
        <h4 className="text-sm font-medium mb-3 text-gray-700">5-inch Display (420x720px)</h4>
        <MediaUploadZone
          label="5-inch Idle Screen"
          target="media5"
          currentMedia={config.icons.media5IdleScreen}
          pendingMedia={pendingMedia5IdleScreen}
          onFileSelect={handleIdleScreenSelect('media5')}
          onRemove={handleIdleScreenRemove('media5')}
          onCrop={handleIdleScreenCrop('media5')}
          helpText="Upload an image or Lottie animation for the 5-inch kiosk idle screen. Images can be cropped after upload."
        />
      </div>

      {/* Info Section */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">About Idle Screens</h4>
        <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
          <li>Idle screens display when no user interaction is detected</li>
          <li>Supports static images (PNG, JPG, SVG, WebP) up to 2MB</li>
          <li>Supports Lottie animations (.json) up to 1MB</li>
          <li>Images can be cropped to fit your desired composition</li>
          <li>Separate media can be uploaded for each display size</li>
        </ul>
      </Card>

      {/* Crop Dialog for Idle Screen Images */}
      {showIdleScreenCropDialog && currentIdleScreenForCrop && currentIdleScreenForCrop.mediaType === 'image' && (
        <LogoCropDialog
          open={showIdleScreenCropDialog}
          onCancel={closeIdleScreenCropDialog}
          imageUrl={currentIdleScreenForCrop.originalImageUrl}
          onConfirm={(croppedBlob, cropArea) => {
            if (idleScreenCropDialogTarget) {
              updateIdleScreenCropArea(idleScreenCropDialogTarget, croppedBlob, cropArea);
              closeIdleScreenCropDialog();
              toast.success('Image cropped', {
                description: 'Crop area applied successfully',
              });
            }
          }}
          initialCropArea={currentIdleScreenForCrop.cropArea}
          title={`Crop ${idleScreenCropDialogTarget === 'media4' ? '4-inch' : '5-inch'} Idle Screen`}
        />
      )}
    </div>
  );
}
